import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import BadgeRow from 'components/BadgeRow';
import Temporal from 'components/Temporal';
import { StopeBackfillSummaryDto, StopeDataDto, StopeLocationDto } from 'providers/api';
import { DEFAULT_SHORT_DATE_CONFIG, NullableProps } from 'utils';
import { i18n } from 'i18n';
import StopeProgress from '../../StopeProgress';
import { Model } from '../../hooks/useModelState';
import { PCNode } from '../../MineModel/Nodes';
import { SceneProps } from '../../Scenes/scene-utils';

const PREFIX = 'StopeListItem';

const classes = {
  listItemTitle: `${PREFIX}-listItemTitle`,
  listItemBodyAction: `${PREFIX}-listItemBodyAction`,
  progress: `${PREFIX}-progress`,
};

const StyledListItemButton = styled(ListItemButton)((
  {
    theme,
  },
) => ({
  [`& .${classes.listItemTitle}`]: {
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: 'rgba(255, 255, 255, 0.87)',
    width: '100%',
  },

  [`& .${classes.listItemBodyAction}`]: {
    fontSize: '12px',
    lineHeight: '12px',
    color: '#9E9E9E',
  },

  [`& .${classes.progress}`]: {
    marginRight: theme.spacing(2),
  },
}));

interface AllStopeProperties extends StopeBackfillSummaryDto, StopeDataDto, StopeLocationDto { }
export interface StopeProfile extends NullableProps<AllStopeProperties> {
  model: Model;
  nodeId: string;
  stopeId: string;
}

interface StopeListItemProps extends StopeProfile {
  onSelect: (model: Model, node: PCNode, props: SceneProps, stopeId?: string) => void;
  onHover: (stopeLocation?: StopeLocationDto) => void;
  props: SceneProps;
}

const StopeListItem = ({
  model,
  stopeId,
  stopeName,
  nodeId,
  totalCount,
  plannedCount,
  completedCount,
  plannedVolume,
  completedVolume,
  nextPourDate,
  isComplete,
  volume,
  onSelect,
  onHover,
  props,
}: StopeListItemProps) => (
  <ListItem disablePadding>
    <StyledListItemButton
      onMouseEnter={() => onHover({ stopeId, nodeId })}
      onMouseLeave={() => onHover()}
      onClick={() => onSelect(model, model.nodes[nodeId], props, stopeId)}
    >
      <ListItemAvatar className={classes.progress}>
        <StopeProgress
          completedVolume={completedVolume ?? 0}
          plannedVolume={plannedVolume ?? 0}
          size={65}
          volume={volume ?? 0}
        />
      </ListItemAvatar>
      <ListItemText
        primary={(
          <>
            <Typography
              className={classes.listItemTitle}
              component="span"
            >
              {stopeName ?? nodeId}
            </Typography>
            <br />
            <Typography
              className={classes.listItemBodyAction}
              component="span"
              variant="body2"
            >
              {isComplete && i18n._('Stope complete')}
              {!isComplete && !nextPourDate && i18n._('No upcoming pour')}
              {!isComplete && nextPourDate && (
                <>
                  {`${i18n._('Next Pour')}: `}
                  <Temporal temporal={nextPourDate} options={DEFAULT_SHORT_DATE_CONFIG} />
                </>
              )}
            </Typography>
          </>
        )}
        secondary={(
          <Box sx={{
            pt: 2,
          }}
          >
            <BadgeRow
              linear
              size="small"
              badgeList={
                [
                  { text: i18n._('Planned:'), total: plannedCount ?? 0 },
                  { text: i18n._('Completed:'), total: completedCount ?? 0 },
                  { text: i18n._('Total:'), total: totalCount ?? 0 },
                ]
              }
            />
          </Box>
        )}
        slotProps={{
          secondary: { component: 'span' },
        }}
      />
    </StyledListItemButton>
  </ListItem>
);
export default StopeListItem;
